import { useSession } from '../state/session.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * The client-side half of the commercial-mode gates — the sibling of
 * `plan-gate.tsx`, asking a different question about the same company.
 *
 * A residential cleaner does not consolidate a month's visits into one
 * invoice, does not cost a standing contract by site, and does not keep a
 * facilities manager's name against a building. Every one of those is a real
 * tab or a real fieldset in this app, and a company that only cleans houses
 * met all of them — a customer form asking about Net 30 and purchase orders
 * before it asks anything a housecleaner would recognize.
 *
 * So the mode decides what is on screen. The API is still the authority where
 * one exists — `POST /change-orders` refuses extra work with no visit (the
 * monthly invoice's kind) from a residential company — and this only decides
 * what is worth showing, which is the same division of labor the plan gates
 * make.
 *
 * Switching Settings → Company to _commercial_ or _both_ brings all of it back
 * in the same render; nothing here is a migration or a one-way door.
 */

/**
 * Whether this company does commercial work at all.
 *
 * A company that has not loaded yet counts as commercial, for the reason
 * `companyHasPlanFeature` counts it as entitled: the alternative is a nav bar
 * that drops an item and puts it back a query later, and a janitorial company
 * watching its own screens flicker. Residential is the stored default, so the
 * answer settles on the truth the moment the session lands.
 */
export function companyDoesCommercialWork(company: Company | null): boolean {
  if (company === null) return true;
  return company.serviceMode !== 'residential';
}

/** The same question from inside a component. */
export function useCommercialWork(): boolean {
  return companyDoesCommercialWork(useSession().company);
}
